<template>
  <transition name="slide-from-bottom">
    <notification-pop-up
      v-if="isOpen && !currentlyOpenModal"
      :notification-type="'info'"
      :has-navigation="!disableOffersPageLink"
      :can-dismiss="true"
      @close-notification="close"
      @notification-click="notificationClickHandler"
    >
      <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-12">
        <template v-if="isMembershipTierStatus">
          <div class="cf-flex cf-flex--column cf-flex--gap-8">
            <p class="cf-subtitle cf-subtitle--3 cf-subtitle--black">
              {{ loyaltyTierName }}
            </p>

            <h4 :class="['cf-title cf-title--4', { 'cf-title--brand-primary': isGreaterThanZero }]">
              {{ rewardCount }} Reward<template v-if="rewardCount !== 1">s</template> Available
            </h4>

            <p class="cf-subtitle cf-subtitle--3 cf-subtitle--black">
              {{ loyalty.tier.description }}
            </p>
          </div>

          <img
            v-if="loyaltyBannerImage"
            class="cf-image cf-image--logo-smaller"
            :alt="loyalty.tier.current.name"
            :src="loyaltyBannerImage"
            onerror="this.src='/images/image-not-found.png';"
          >
        </template>

        <template v-else>
          <cf-loyalty-progress-ring v-if="showPersonalLoyaltyInfo" />

          <div class="cf-flex cf-flex--column cf-flex--gap-8">
            <p
              v-if="loyaltyTierName"
              class="cf-subtitle cf-subtitle--2"
            >
              {{ loyaltyTierName }}
            </p>

            <h2 class="cf-title cf-title--2">
              {{ loyalty.currentBalance }} {{ loyalty.currency.pluralNameCapitalCase }}
            </h2>

            <template v-if="rewardWasJustEarned">
              <h2 class="cf-title cf-title--2">
                You earned a reward!
              </h2>

              <p class="cf-subtitle cf-subtitle--3">
                You earned {{ numberOfPointsJustEarned }} {{ loyalty.currency.pluralName }} and <b class="cf-subtitle--heavy">{{ rewardTitle }}</b> on your next order.
              </p>
            </template>

            <template v-else>
              <h2
                v-if="numberOfPointsJustEarned"
                class="cf-title cf-title--2"
              >
                You earned {{ numberOfPointsJustEarned }} {{ loyalty.currency.pluralName }}!
              </h2>

              <template v-if="showPersonalLoyaltyInfo">
                <h4 :class="[ 'cf-title cf-title--4', { 'cf-subtitle--brand-primary': isGreaterThanZero }]">
                  {{ rewardCount }} Reward<template v-if="rewardCount !== 1">s</template> Available
                </h4>

                <p
                  v-if="loyalty.pointsUntilNextReward"
                  class="cf-subtitle cf-subtitle--3 cf-subtitle--black"
                >
                  {{ loyalty.pointsUntilNextReward }} {{ loyalty.currency.pluralName }} until your next reward.
                </p>

                <p
                  v-else
                  class="cf-subtitle cf-subtitle--3 cf-subtitle--black"
                >
                  You've unlocked every reward!
                </p>
              </template>
            </template>
          </div>
        </template>
      </div>
    </notification-pop-up>
  </transition>
</template>


<script>
  import { mapGetters } from 'vuex';

  import events from '@/services/events';

  import NotificationPopUp from '../../shared/notification-pop-up/index.vue';
  import CfLoyaltyProgressRing from '../../shared/cf-loyalty-progress-ring/index.vue';


  export default {
    name: 'CfLoyaltyNotification',

    components: {
      NotificationPopUp,
      CfLoyaltyProgressRing
    },

    data() {
      return {
        disableOffersPageLink: false,
        hasAutoOpened: false,
        isOpen: false,
        numberOfPointsJustEarned: 0,
        rewardTitle: null,
        rewardWasJustEarned: false,
        showPersonalLoyaltyInfo: true
      };
    },

    computed: {
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('modals', ['currentlyOpenModal']),
      ...mapGetters('offers', ['loyalty', 'rewardCount']),
      ...mapGetters('session', ['currentUser', 'orderDetails']),

      loyaltyTierName() {
        return this.loyalty.tier?.current?.name;
      },

      isMembershipTierStatus() {
        return this.loyalty.isMembershipTierStatus;
      },

      loyaltyBannerImage() {
        return this.loyalty.tier?.current?.badgeDefaultUrl;
      },

      isGreaterThanZero() {
        return this.rewardCount > 0;
      }
    },

    created() {
      this.createdHandler();
    },

    unmounted() {
      this.destroyedHandler();
    },

    methods: {
      autoOpen() {
        if (this.hasAutoOpened) {
          return true;
        }

        this.open();
      },

      close() {
        this.isOpen = false;
        this.numberOfPointsJustEarned = 0;
        this.rewardTitle = null;
        this.rewardWasJustEarned = false;

        events.off('route', this.close);
      },

      createdHandler() {
        events.on('loyalty-popup-card:open', this.openHandler);
        events.on('modals:open', this.close);
        events.on('signed-in', this.signInHandler);
        events.on('signed-out', this.close);
      },

      destroyedHandler() {
        events.off('loyalty-popup-card:open', this.openHandler);
        events.off('modals:open', this.close);
        events.off('signed-in', this.signInHandler);
        events.off('signed-out', this.close);
      },

      notificationClickHandler() {
        if (!this.disableOffersPageLink) {
          this.$router.push({
            name: 'loyalty',
            params: {
              merchantId: this.$route.params.merchantId
            }
          });
        }
      },

      async open() {
        this.isOpen = true;
        this.hasAutoOpened = true;

        events.on('route', this.close);
      },

      async openHandler({ disableOffersPageLink, receipt, refreshData, autoOpen } = {}) {
        this.disableOffersPageLink = disableOffersPageLink || !this.currentUser.accountId || false;

        if (refreshData) {
          await this.$store.dispatch('offers/getOffers', {
            silentError: true
          });
        }

        if (receipt) {
          this.disableOffersPageLink = disableOffersPageLink || !receipt.isLinkedToCurrentAuthenticatedUser;
          this.showPersonalLoyaltyInfo = receipt.isLinkedToCurrentAuthenticatedUser;

          if (receipt.rewardsSummary) {
            const { earnedNewReward, offerTitle, pointsJustEarned } = receipt.rewardsSummary;

            this.rewardWasJustEarned = earnedNewReward;

            this.rewardTitle = offerTitle;

            this.numberOfPointsJustEarned = pointsJustEarned;
          }
        }

        if ((!this.currentUser.accountId || !this.loyalty.isEnrolled) && !receipt?.loyaltyEngagementSummary) {
          /**
          * don't show the loyalty popup if there is no logged in user
          * or the logged in user is not enrolled in loyalty
          * unless the receipt contains loyalty data
          */
          return;
        }

        if (autoOpen) this.autoOpen();
        else this.open();
      },

      signInHandler() {
        const { loyaltyPopupOnSignInEnabled } = this.merchant.options.loyaltyOptions;
        const isPayAtTable = ['pay', 'table'].includes(this.orderDetails.orderType);

        if (this.loyalty.isEnrolled && loyaltyPopupOnSignInEnabled && !isPayAtTable) {
          this.open();
        }
      }
    }
  };
</script>
